/**
 * Coquille de dialogue réutilisable pour les formulaires applicatifs (ProjectForm…).
 * Reprend la recette des dialogs (design system §8) : overlay `bg-overlay`, cadre 2px
 * à équerres, fermeture Échap (capture + stopPropagation) et clic extérieur, focus piégé.
 *
 * Différent de `DialogHost` (service.js) : celui-ci sert aux confirmations/prompts génériques,
 * `Modal` sert de coquille à un contenu arbitraire (formulaire) fourni par l'appelant.
 */

import { useEffect, useId, useRef } from 'react';
import { EYEBROW } from '../ui/theme.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Largeurs disponibles — classes littérales, pour que Tailwind les voie. `md` est la largeur
 * historique des formulaires ; `lg` (le double) sert aux contenus de consultation en liste,
 * comme la vue du jour du planning.
 */
const WIDTHS = {
  md: 'w-[520px]',
  lg: 'w-[1040px]',
};

export function Modal({ title, onClose, children, size = 'md' }) {
  const boxRef = useRef(null);
  const titleId = useId();

  // Échap en capture : ne doit pas se propager jusqu'à un panneau derrière la boîte.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // Un dialogue du service (`DialogHost`, marqué `data-dialog-service`) ouvert par-dessus
      // cette boîte garde Échap pour lui : les deux écouteurs sont en capture sur `document`,
      // `stopPropagation` n'empêcherait pas celui-ci de fermer aussi la boîte.
      if (document.querySelector('[data-dialog-service]')) return;
      event.stopPropagation();
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // Focus piégé : Tab/Shift+Tab bouclent parmi les éléments focalisables de la boîte.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;

    const focusables = () => Array.from(box.querySelectorAll(FOCUSABLE));
    const first = focusables()[0];
    (first || box).focus();

    const onTrap = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const [firstItem, lastItem] = [items[0], items[items.length - 1]];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };
    box.addEventListener('keydown', onTrap);
    return () => box.removeEventListener('keydown', onTrap);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`${WIDTHS[size] ?? WIDTHS.md} max-w-full bg-paper p-3 shadow-plank`}
      >
        <div className="relative border-2 border-line p-4">
          <span className="ticks" aria-hidden="true" />
          <p id={titleId} className={EYEBROW}>
            {title}
          </p>
          <div className="mt-3">{children}</div>
        </div>
      </div>
    </div>
  );
}
